import { afterNextRender, Component, ElementRef, inject, input, Renderer2, signal, viewChildren } from "@angular/core";
import { ResourceType } from "src/app/models/api/common";
import { ResourceCategory } from "src/app/models/api/inventoryResource.model";
import { CardFieldKey, ICardField } from "src/app/models/viewModels/cardModel";

@Component({
    selector: "app-card-fields",
    templateUrl: "./card-fields.component.html",
    styleUrls: ["./card-fields.component.scss"],
})
export class CardFieldsComponent {
    public cardFields = input.required<ICardField[]>();
    public verticalOrder = input<boolean>(false);
    public resourceType = input<ResourceType>();
    public resourceCategory = input<ResourceCategory>();
    public overflowHorizontalItems = signal<boolean[]>([]);
    public overflowVerticalConversationStartersBlock = signal<boolean>(false);

    public resourceTypeEnum = ResourceType;
    public resourceCategoryEnum = ResourceCategory;
    public readonly conversationStartersFieldName = CardFieldKey.ConversationStarters;
    public readonly resourceDescriptionFieldName = CardFieldKey.ResourceDescription;
    public readonly instructionsFieldName = CardFieldKey.Instructions;

    private readonly _gptAdditionalInfoFieldsWithCopyButton = [
        "Access",
        "Categories",
        "Conversation Starters",
        "Dalle Capability",
        "Web Browsing Capability",
        "Canvas Capability",
        "Code Interpreter Capability",
        "GPT chat URL",
    ];
    private readonly _gptOtherFieldsWithCopyButton = ["Instructions"];
    public readonly copyableFields = [...this._gptAdditionalInfoFieldsWithCopyButton, ...this._gptOtherFieldsWithCopyButton];

    public readonly truncatedMaxHeightForConversationStartersBlock = 60;

    private _elements = viewChildren<ElementRef<HTMLElement>>("fieldEl");

    private _renderer: Renderer2 = inject(Renderer2);

    constructor() {
        afterNextRender(() => {
            this.overflowHorizontalItems.set([]);
            this.overflowVerticalConversationStartersBlock.set(false);

            const elements = this._elements();

            this._setOverflowHorizontalItems(elements);
            this._setOverflowVerticalConversationStartersBlock(elements);
        });
    }

    public isDictSeverityNotEmpty(dictSeverity: Record<string, number> | undefined): boolean {
        if (dictSeverity && Object.keys(dictSeverity).length > 0) {
            return true;
        } else {
            return false;
        }
    }

    public getClipboardText(value: string, additionalValues: string[] | undefined): string {
        return additionalValues?.length ? `${value}, ${additionalValues.join(", ")}` : value;
    }

    public shouldRenderAsHtml(cardField: ICardField): boolean {
        return (
            this.resourceType() === this.resourceTypeEnum.gpt &&
            cardField.key === this.conversationStartersFieldName &&
            !this.overflowVerticalConversationStartersBlock()
        );
    }

    private _checkHorizontalOverflow(element: HTMLElement): boolean {
        const clone = this._createElementClone(element);

        // Width measurement couldn't be done without adding element clone to the DOM
        this._renderer.appendChild(document.body, clone);

        const fullWidth = clone.scrollWidth;

        const paddingOverlap = 20; // Adjust for paddings and margins
        const visibleWidth = element.offsetWidth - paddingOverlap;

        this._renderer.removeChild(document.body, clone);

        return fullWidth > visibleWidth;
    }

    // Makes copy of the element to safely measure the full text width of content
    // Removes width constraints and hides the clone off-screen
    private _createElementClone(element: HTMLElement): HTMLElement {
        const clone = element.cloneNode(true) as HTMLElement;

        this._renderer.setStyle(clone, "max-width", "none");
        this._renderer.setStyle(clone, "width", "auto");

        this._renderer.setStyle(clone, "white-space", "nowrap");
        this._renderer.setStyle(clone, "position", "absolute");
        this._renderer.setStyle(clone, "visibility", "hidden");

        return clone;
    }

    private _setOverflowHorizontalItems(elements: readonly ElementRef<HTMLElement>[]): void {
        const flags = elements.map((elRef) => this._checkHorizontalOverflow(elRef.nativeElement));
        this.overflowHorizontalItems.set(flags);
    }

    private _setOverflowVerticalConversationStartersBlock(elements: readonly ElementRef<HTMLElement>[]): void {
        const idx = this.cardFields().findIndex((f) => f.key === this.conversationStartersFieldName);
        if (idx >= 0) {
            const el = elements[idx].nativeElement;
            this.overflowVerticalConversationStartersBlock.set(el.scrollHeight > this.truncatedMaxHeightForConversationStartersBlock);
        } else {
            this.overflowVerticalConversationStartersBlock.set(false);
        }
    }
}
